import { Component, lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { ErrorScreen } from '../components/ErrorScreen';
import { Loading } from '../components/Loading';
import { isChunkError, reportCrash } from './crashReport';

// Pages load when first opened, so the first download is only the shell and chats. Once the app is idle,
// every page is fetched in the background: the next tap is instant, and the service worker has them all
// cached for offline use.

const loaders: Array<() => Promise<unknown>> = [];
const RELOAD_KEY = 'da_chunk_reload';


function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  location.reload();
  return true;
}

export function lazyPage<M, K extends keyof M>(load: () => Promise<M>, name: K): ComponentType {
  loaders.push(load);
  return lazy(async () => {
    try {
      return { default: (await load())[name] as ComponentType };
    } catch (err) {
      // Never resolves while the page reloads, so no error flashes first.
      if (navigator.onLine && isChunkError(err) && reloadOnce()) return new Promise<never>(() => undefined);
      throw err;
    }
  });
}

/**
 * A page needed right after start for most people (the shell and the chats): its own file, so the sign-in pages
 * open without it, but its download starts at once, beside the session check, so nobody waits for it later.
 */
export function eagerPage<M, K extends keyof M>(load: () => Promise<M>, name: K): M[K] {
  const started = load();
  started.catch(() => undefined); // reported when the page is shown
  return lazy(async () => {
    try {
      return { default: (await started)[name] as ComponentType };
    } catch (err) {
      if (navigator.onLine && isChunkError(err) && reloadOnce()) return new Promise<never>(() => undefined);
      throw err;
    }
  }) as unknown as M[K];
}

/** Fetches every page in the background when the browser is idle (skipped on data saver). */
export function prefetchPages(): void {
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (conn?.saveData) return;
  const run = () => {
    let i = 0;
    const next = () => {
      const load = loaders[i++];
      if (load) load().then(next, next);
    };
    next();
  };
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  if (idle) idle(run, { timeout: 4000 });
  else setTimeout(run, 2000);
}

/** A page still downloading: nothing for a moment (usually it is already there), then its outline. */
function PageLoading() {
  return <Loading variant="card" />;
}

function PageFailed() {
  // A page file that could not be downloaded is usually the connection; anything else broke on the page.
  return <ErrorScreen kind={navigator.onLine ? 'crashed' : 'offline'} />;
}

class PageErrorBoundary extends Component<{ children: ReactNode; resetKey: string }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(err: unknown) {
    reportCrash(err);
  }

  override componentDidUpdate(prev: { resetKey: string }) {
    if (prev.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false });
  }

  override render() {
    return this.state.failed ? <PageFailed /> : this.props.children;
  }
}

/** Wraps lazily loaded pages: a quiet loading state, and a retry instead of a blank screen when a page cannot load. */
export function PageBoundary({ children, resetKey }: { children: ReactNode; resetKey: string }) {
  return (
    <PageErrorBoundary resetKey={resetKey}>
      <Suspense fallback={<PageLoading />}>{children}</Suspense>
    </PageErrorBoundary>
  );
}
